import RadioGroup from '@oncolens/oncolens-ui/lib/components/FormControls/RadioGroup';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import { useHydrateStore } from '@oncolens/oncolens-ui/lib/hooks/common/useHydrateForm';
import clsx from 'clsx';
import React from 'react';
import { FaRegCheckCircle, FaRegCircle } from 'react-icons/fa';
import shallow from 'zustand/shallow';
import {
  StagingDto,
  StagingForUpdateDto,
  StagingRowsForUpdateDto,
  StagingValuesDto,
} from '../dtos/Staging';
import { stagingKeys } from './staging.keys';
import { createStaging, getStaging, updateStaging } from '../hooks/useStaging';
import useRequestFormStore from '../store';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import { SimpleStagingForm } from './SimpleStagingForm';
import StagingTable from './StagingTable';
import { defaultTemplate, storyTemplates } from './StagingTemplates';
import { StagingData, StagingTemplates } from './StagingTypes';
import { noop } from 'lodash';

// TODO this is really domain logic to live somewhere central
function GetTemplateFromCancerType(cancerType: string | undefined) {
  let templateName = 'default' as typeof StagingTemplates[number];
  if (cancerType === 'bbba0e5c-5b67-44aa-aa8e-fba915a2b810') {
    templateName = 'breast';
  }

  if (cancerType === 'f89d483c-34d5-49d9-aa7f-280f742d61bf') {
    templateName = 'liver';
  }

  if (cancerType === 'c2f2baec-e280-4d96-86d9-1a113f8e2e0b') {
    templateName = 'brain';
  }

  if (cancerType === 'd61341cd-f353-4227-88d2-2784230697c5') {
    templateName = 'hematologic';
  }

  if (cancerType === '8cf10b1f-5fe6-4c72-8dab-d8a20d073f6f') {
    templateName = 'lymph';
  }

  return templateName;
}

// Temp export, only needs to be exported until API is hooked up
export function useTemplate(templateName: typeof StagingTemplates[number]) {
  const template = storyTemplates.find((t) => t.name === templateName) ?? defaultTemplate;

  const columns = React.useMemo(() => template.columns, [template.columns]);
  return columns;
}

export const mapStaging = (staging: StagingDto[] | undefined) => {
  if (!staging) {
    return {};
  }
  const recordSet = staging[0];
  if (!recordSet) {
    return {};
  }

  const ret = recordSet.stagingRowsAndValues.reduce((acc: any, row) => {
    const timingId = row.stagingTimingId;
    const values = row.stagingValues.reduce((acc: any, keyValuePair) => {
      acc[keyValuePair.key] = keyValuePair.value;
      return acc;
    }, {});
    acc[timingId] = values;
    return acc;
  }, {});

  ret.id = recordSet.id;
  ret.bclcStage = recordSet.bclcStage;
  ret.tumorSite = recordSet.tumorSite;
  ret.stagingDetails = recordSet.stagingDetails;

  return ret;
};

const clinical = '837e5af3-14e9-4325-93a4-67cb6f0da741';
const pathological = '2b1ea756-5758-4b6b-ab30-2e7315024739';
const postTherapy = 'b285796c-ea85-4428-bc4a-d00cdd73e12f';
const seedStagingData = () =>
  [
    {
      id: clinical,
      timing: 'Clinical',
    },
    {
      id: pathological,
      timing: 'Pathological',
    },
    {
      id: postTherapy,
      timing: 'Post-therapy',
    },
  ] as StagingData[];

interface StagingFormProps {
  episodeId: string;
  diseaseId: string;
  isInAutosave: boolean;
}

function StagingForm({ diseaseId, episodeId, isInAutosave }: StagingFormProps) {
  const formKey = `diseases.${diseaseId}.episodes.${episodeId}.staging`;
  const [disease, updateField, getValues] = useRequestFormStore(
    (store) => [store.diseases[diseaseId], store.updateField, store.getValues],
    shallow
  );

  const templateName = GetTemplateFromCancerType(disease.cancerTypeId);

  const { isLoading, isHydrated } = useHydrateStore({
    apiCall: async () => getStaging(episodeId),
    keys: { cacheKey: stagingKeys.staging(episodeId), formKey },
    updateStore: updateField,
    shouldRun: isInAutosave,
    map: (staging: StagingDto[] | undefined) => {
      return mapStaging(staging);
    },
  });

  // Temp until api call
  const columns = useTemplate(templateName);

  return (
    <div>
      <div className="mb-4">
        <FormField
          name={`${formKey}.stagingDetails`}
          store={useRequestFormStore}
          validationOptions={{
            valueProvider: getValues,
          }}
        >
          {({ value, onChange }) => (
            <TextAreaInput
              id={`stagingDetails`}
              label={'Staging Details'}
              htmlName={`stagingDetails`}
              placeholder={'Add other details related to staging'}
              value={value}
              onChange={onChange}
            />
          )}
        </FormField>
      </div>
      <h3 className="mb-1 font-semibold">AJCC Staging</h3>
      <div className="w-full mb-4 overflow-hidden overflow-x-auto">
        <StagingTable
          data={seedStagingData()}
          columns={columns}
          tableState={'edit'}
          formKey={formKey}
        />
      </div>
      {/* Tumor Site (for brain) */}
      {templateName === 'brain' && (
        <div>
          <div className={'col-span-8 sm:col-span-4'}>
            <label
              htmlFor="Unifocal"
              className="inline-block mb-2 text-sm font-medium text-gray-700"
            >
              Tumor Site
            </label>
            <FormField name={`${formKey}.tumorSite`} store={useRequestFormStore}>
              {({ value, onChange }) => (
                <RadioGroup
                  className="flex items-center mb-5 space-x-2"
                  selected={value}
                  setSelected={onChange}
                >
                  <RadioGroup.Option id={'Unifocal'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="tumor-site-unifocal"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>Unifocal</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'Multifocal'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="tumor-site-multifocal"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>Multifocal</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                </RadioGroup>
              )}
            </FormField>
          </div>
        </div>
      )}
      {/* BCLC Stage (for liver) */}
      {templateName === 'liver' && (
        <div>
          <div className="max-w-xl pt-3">
            <label
              htmlFor="bclcStage"
              className="inline-block mb-2 text-sm font-medium text-gray-700"
            >
              BCLC Stage
            </label>
            <FormField name={`${formKey}.bclcStage`} store={useRequestFormStore}>
              {({ value, onChange }) => (
                <RadioGroup
                  groupId="bclcStage"
                  className="grid grid-cols-4 gap-2 lg:grid-cols-none lg:grid-flow-col lg:auto-cols-auto"
                  selected={value}
                  setSelected={onChange}
                >
                  <RadioGroup.Option id={'1A'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-1a"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>1A</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'1B'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-1b"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>1B</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'II'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-ii"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>II</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'IIIA'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-iiia"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>IIIA</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'IIIB'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-iiib"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>IIIB</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'IVA'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-iva"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>IVA</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                  <RadioGroup.Option id={'IVB'}>
                    {(active, onClick) => (
                      <div
                        role="radiogroup"
                        tabIndex={-1}
                        onKeyDown={noop}
                        data-cy="bclc-stage-ivb"
                        className={clsx(
                          'flex items-center px-4 py-3 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                          active &&
                            'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                        )}
                        onClick={() => {
                          onClick();
                        }}
                      >
                        {active ? (
                          <FaRegCheckCircle className="mr-2 text-white" />
                        ) : (
                          <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                        )}
                        <p>IVB</p>
                      </div>
                    )}
                  </RadioGroup.Option>
                </RadioGroup>
              )}
            </FormField>
          </div>
        </div>
      )}

      {/* TODO: add back when we put in simple staging */}
      {/* <SimpleStagingForm diseaseId={diseaseId} episodeId={episodeId} /> */}
    </div>
  );
}

const generatePayload = (requestData: any): StagingForUpdateDto => {
  const { id, bclcStage, tumorSite, stagingDetails, ...rowsAndValues } = requestData;

  const stagingRowsAndValues: StagingRowsForUpdateDto[] = Object.entries(rowsAndValues).map(
    ([timingId, values]) => {
      return {
        stagingTimingConfigId: timingId,
        stagingValues: Object.entries(values as any).map(([key, value]) => {
          return {
            key: key,
            value: value,
          } as StagingValuesDto;
        }),
      } as StagingRowsForUpdateDto;
    }
  );

  const payload = {
    id,
    bclcStage,
    tumorSite,
    stagingDetails,
    stagingRows: stagingRowsAndValues,
  } as StagingForUpdateDto;

  return payload;
};

StagingForm.create = async (episodeId: string, staging: any) => {
  const payload = generatePayload(staging);

  return createStaging(episodeId, payload);
};

StagingForm.update = async (diseaseId: string, episodeId: string) => {
  const data = useRequestFormStore.getState().diseases[diseaseId].episodes[episodeId].staging;
  const payload = generatePayload(data);

  return updateStaging(payload.id, payload);
};

StagingForm.createOrUpdate = async (diseaseId: string, episodeId: string) => {
  const { updateField, diseases } = useRequestFormStore.getState();
  const episode = diseases[diseaseId].episodes[episodeId];

  if (episode.staging.id) {
    return updateStaging(episode.staging.id, generatePayload(episode.staging));
  } else {
    return createStaging(episodeId, generatePayload(episode.staging)).then((result) => {
      updateField(`diseases.${diseaseId}.episodes.${episodeId}.staging.id`, result.id);
    });
  }
};

export default StagingForm;
